import { AppContext, Organisation, OrganisationPage, TimeSlot } from "../../../AppDataProvider/AppDataProvider";
import { LoadingPill as BaseLoadingPill, Notification } from "../../../common/components";
import { corners, marginBottom, marginRight, marginTop, padding, paddingBottom, paddingTop, paddingY, typography } from "../../../common/styles";
import { findTimeZone, getZonedTime } from "timezone-support";
import { getClosestTimeSlots, useClientLocation } from "../../../utils";
import { parse, set } from "date-fns";
import React, { useContext, useEffect, useState } from "react";
import { ActionType } from "../../../AppDataProvider/actions";
import { OnSelectOrganisationTimeSlotOpts } from "../LocationAvailabilityStep";
import styled from "styled-components";
import { TimeSlotSelection } from "../TimeSlotSelection/TimeSlotSelection";
import { UnitOfMeasurement } from "../../../GeneratedGraphQL";
import { useGetOrganisationsAvailability } from "../hooks/useGetOrganisationsAvailability";
import { useTranslation } from "react-i18next";
import { zonedTimeToUtc } from "date-fns-tz";
export type OrganisationsListProps = {
  createReservationLoading: boolean;
  filteredOrganisations: OrganisationPage;
  onSelectOrganisationTimeSlot: (options: OnSelectOrganisationTimeSlotOpts) => void;
  prefilledOrganisationId?: string;
  queryByOrganisationTimeZone: boolean;
  selectedDate: string;
  selectedOrganisationFromMap?: Organisation | null;
  selectedTime: string;
};
export function OrganisationsList(props: OrganisationsListProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const {
    createReservationLoading,
    filteredOrganisations,
    onSelectOrganisationTimeSlot,
    prefilledOrganisationId,
    queryByOrganisationTimeZone,
    selectedDate,
    selectedOrganisationFromMap,
    selectedTime
  } = props;
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const {
    bookingData,
    bookingTool
  } = appData;
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedOrganisationFromTimeSlot, setSelectedOrganisationFromTimeSlot] = useState<Organisation | null>(null);
  const filteredOrganisationIds: string[] = filteredOrganisations.edges.map(({
    node
  }) => node._id);
  const {
    organisationsAvailability,
    loading,
    error
  } = useGetOrganisationsAvailability({
    bookingData,
    bookingTool,
    ...(prefilledOrganisationId ? {
      filteredOrganisationIds: [prefilledOrganisationId]
    } : {
      filteredOrganisationIds
    }),
    queryByOrganisationTimeZone,
    selectedDate
  });
  useEffect(() => {
    if (error) {
      appDataDispatch({
        payload: {
          message: t("errors.availabilityRequestFailed"),
          variant: "warning"
        },
        type: ActionType.ADD_TOAST
      });
    }
  }, [appDataDispatch, error, t]);
  const organisationTimeSlotSelected = (selectedOrganisation: Organisation, selectedTimeSlot: TimeSlot) => {
    setValidationError(null);
    setSelectedTimeSlot(selectedTimeSlot);
    setSelectedOrganisationFromTimeSlot(selectedOrganisation);
    if (selectedTimeSlot) {
      onSelectOrganisationTimeSlot({
        selectedOrganisation,
        selectedTimeSlot
      });
    }
  };
  const [hours, minutes] = selectedTime.split(":").map(Number);
  const parsedDate = parse(selectedDate, "yyyy-MM-dd", new Date());
  const selectedDateTime = set(parsedDate, {
    hours,
    minutes
  });
  const getTimezoneAbbreviation = (timezone: string, date: Date) => {
    const tz = findTimeZone(timezone);
    const time = getZonedTime(date, tz);
    return time.zone?.abbreviation;
  };
  const {
    timezone: clientTimezone
  } = useClientLocation({
    fallbackLocation: "GB",
    fallbackTimezone: "Europe/London"
  });
  const userAbbvTz = getTimezoneAbbreviation(clientTimezone ? clientTimezone : "Europe/London", selectedDateTime);
  const tzAllMatchOrg = organisationsAvailability.every(org => {
    const orgDetails = filteredOrganisations.edges.find(o => o.node._id == org.organisation._id);
    const orgAbbvTz = getTimezoneAbbreviation(orgDetails ? orgDetails.node.defaultTimeZone : "Europe/London", selectedDateTime);
    return orgAbbvTz == userAbbvTz;
  });
  const listOfOrganisationsOrSinglePrefilledOrganisation = prefilledOrganisationId ? {
    edges: bookingTool.organisations.edges.filter(organisation => organisation.node._id === prefilledOrganisationId)
  } : filteredOrganisations;
  if (loading) {
    return <LoadingPill />;
  }
  if (organisationsAvailability.length === 0) return <Container />;
  return <Container data-sentry-element="Container" data-sentry-component="OrganisationsList" data-sentry-source-file="OrganisationsList.tsx">
      <Title data-sentry-element="Title" data-sentry-source-file="OrganisationsList.tsx">{t("organisationsAvailabilityTitle")}</Title>
      {!tzAllMatchOrg && <Label>{t("timeZoneMessage")}</Label>}
      {organisationsAvailability && listOfOrganisationsOrSinglePrefilledOrganisation.edges.map((filteredOrg, index) => {
      const orgAvailability = organisationsAvailability.find(org => {
        return org.organisation._id === filteredOrg.node._id;
      });
      const formattedSelectedDateTime = zonedTimeToUtc(selectedDateTime, filteredOrg.node.defaultTimeZone);
      const filteredTimeSlots = getClosestTimeSlots(formattedSelectedDateTime, orgAvailability ? orgAvailability.timeSlots : [], 10);
      const orgAbbvTz = getTimezoneAbbreviation(filteredOrg?.node.defaultTimeZone, selectedDateTime);
      return prefilledOrganisationId ? <SingleOrganisationCard key={filteredOrg.node._id}>
                {filteredTimeSlots && filteredTimeSlots.length === 0 && <NoAvailability>{t("noAvailabilityForDay")}</NoAvailability>}
                {filteredTimeSlots && filteredTimeSlots.length > 0 && <TimeSlotSelection createReservationLoading={createReservationLoading} timeSlots={filteredTimeSlots} onSelectTimeSlot={timeSlot => organisationTimeSlotSelected(filteredOrg.node, timeSlot)} organisation={filteredOrg.node} queryByOrganisationTimeZone selectedTimeSlot={selectedTimeSlot} selectedOrganisation={selectedOrganisationFromTimeSlot} />}
              </SingleOrganisationCard> : <OrganisationCard id={filteredOrg.node._id} key={filteredOrg.node._id} selected={filteredOrg.node._id === selectedOrganisationFromMap?._id}>
                <OrganisationInformation>
                  <OrganisationCore>
                    <OrganisationName>
                      <OrgLabel>
                        {index + 1}. {filteredOrg.node.name}
                      </OrgLabel>
                    </OrganisationName>

                    {filteredOrg?.node.proximityToSearchedLocation && <Distance>
                        <Label>{t("distance")}</Label>
                        <Value>
                          {`${filteredOrg.node.proximityToSearchedLocation >= 10 ? filteredOrg.node.proximityToSearchedLocation.toFixed(0) : filteredOrg.node.proximityToSearchedLocation.toFixed(1)} ${appData.bookingTool.unitOfMeasurement === UnitOfMeasurement.Miles ? t("miles") : t("kilometres")}`}
                        </Value>
                      </Distance>}
                  </OrganisationCore>
                  {filteredOrg?.node.location && <Location>{filteredOrg?.node.location.address}</Location>}
                </OrganisationInformation>
                {filteredTimeSlots && filteredTimeSlots.length === 0 && <NoAvailability>{t("noAvailabilityForDay")}</NoAvailability>}
                {filteredTimeSlots && filteredTimeSlots.length > 0 && <TimeSlotSelection createReservationLoading={createReservationLoading} timeSlots={filteredTimeSlots} onSelectTimeSlot={timeSlot => organisationTimeSlotSelected(filteredOrg.node, timeSlot)} organisation={filteredOrg.node} queryByOrganisationTimeZone selectedTimeSlot={selectedTimeSlot} selectedOrganisation={selectedOrganisationFromTimeSlot} />}
                {!loading && !tzAllMatchOrg && <Label>
                    {t("organisationTimezoneMessage", {
            organisationTimezone: orgAbbvTz
          })}
                  </Label>}
              </OrganisationCard>;
    })}
      {validationError && <ErrorNotification variant="warning">
          {validationError}
        </ErrorNotification>}
    </Container>;
}
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
`;
const Title = styled.div`
  ${typography.bodyBold}
  ${paddingBottom.xs}
  /*
   * Was unset — this heading renders directly inside the plain Container
   * (which sets no ambient colour), so it silently fell back to the
   * browser default black rather than the theme's text colour.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const OrganisationCard = styled.div<{
  selected?: boolean;
}>`
  display: flex;
  flex-direction: column;
  ${marginTop.m};
  border: 2px solid
    ${({
  theme,
  selected
}) => selected ? theme.brand.cssVars["--d-primary"] : theme.brand.cssVars["--d-border"]};
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
  ${corners.m}
  ${padding.m};
`;
const SingleOrganisationCard = styled.div`
  display: flex;
  ${paddingTop.m};
`;
const OrganisationInformation = styled.div`
  display: flex;
  flex-direction: column;
  ${paddingBottom.m};
`;
const OrganisationName = styled.div`
  ${typography.bodyBold}
`;
const NoAvailability = styled.div`
  ${typography.body}
  text-align: center;
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
  ${paddingY.s}
`;
const ErrorNotification = styled(Notification)`
  ${marginTop.m}
`;
const LoadingPill = styled(BaseLoadingPill)`
  align-self: center;
  margin: auto;
`;
const OrganisationCore = styled.div`
  ${marginBottom.xs};
  display: flex;
  justify-content: space-between;
`;
const Location = styled.div`
  ${typography.body}
`;
const Distance = styled.div``;
const Value = styled.span`
  ${typography.small}
`;
const OrgLabel = styled.span`
  ${typography.small}
  ${marginRight.s}
  ${typography.bodyBold}
`;
const Label = styled.span`
  ${typography.small}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
  ${marginRight.s}
`;